import { Button, Group, Table, Tooltip } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { KeyedRow } from '../../components/KeyedRow';
import { PageHeader } from '../../components/PageHeader';
import { Title } from '../../components/Title';
import { useFilter } from '../../hooks/useFilter';
import { useModal } from '../../hooks/useModal';
import { usePagination } from '../../hooks/usePagination';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { queryWithFilter } from '../../util/api/queryWithFilter';
import { queryWithFilterAndPages } from '../../util/api/queryWithFilterAndPages';
import { ExportButton } from '../export/ExportButton';
import { NewPrescriptionModal } from './components/NewPrescriptionModal';
import { PrescriptionRow } from './components/PrescriptionRow';
import { usePrescriptionAuth } from './hooks/usePrescriptionAuth';
import type { PrescriptionDto } from './types/PrescriptionDto';

export function Prescriptions() {
	const { organisation } = useParams();
	const filter = useFilter();
	const pagination = usePagination();

	const prescriptionsQuery = queryWithFilterAndPages<PrescriptionDto[]>(
		`${organisation}/prescriptions/all`,
		filter.value,
		pagination.value,
	);

	const prescriptionsCountQuery = queryWithFilter<number>(
		`${organisation}/prescriptions/count`,
		filter.value,
	);

	const newModal = useModal();

	const { authMessage } = usePrescriptionAuth();

	const refreshTable = () => {
		prescriptionsQuery.remove();
		prescriptionsCountQuery.remove();

		prescriptionsQuery.refetch();
		prescriptionsCountQuery.refetch();
	};

	return (
		<>
			<NewPrescriptionModal
				close={newModal.close}
				opened={newModal.isOpen}
				refreshTable={refreshTable}
			/>
			<PageHeader
				title={'Prescriptions'}
				filter={filter}
				filterOnSameLine
				rightContent={
					<Group>
						<ExportButton
							apiPath={`${organisation}/prescriptions/export`}
							disablePdf
						/>
						<Tooltip label={authMessage} hidden={authMessage === undefined}>
							<Button
								{...whiteIconButtonStyle}
								data-cy={'create-iconButton'}
								onClick={() => newModal.open()}
								disabled={authMessage !== undefined}
							>
								<IconPencilPlus {...defaultIconStyle} />
							</Button>
						</Tooltip>
					</Group>
				}
			/>
			<DataTable
				pagination={pagination}
				totalItems={prescriptionsCountQuery.data?.value ?? 100}
				isRowsLoading={prescriptionsQuery.isLoading}
				isPaginationLoading={prescriptionsCountQuery.isLoading}
				rows={pagination.value.pageSize}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Title label={'Date'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Branch Name'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Forms'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Items'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Daily Installments'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Weekly Installments'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Methadone Patients No'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Verified By'} />
						</Table.Th>
						<Table.Th>
							<Title label={'View'} />
						</Table.Th>
						<Table.Th />
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{prescriptionsQuery.data?.value.map((prescription) => (
						<PrescriptionRow
							key={prescription.pkid}
							initialPrescription={prescription}
							refreshTable={refreshTable}
						/>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
